import React, { useRef } from "react";
import { Document, Page, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import { now } from "moment";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_TIME_FORMAT } from "src/common/constants";

import { registerExportFonts } from "./document/fonts";
import { BrandingProvider, useBranding } from "./document/BrandingContext";
import {
    CoverPage,
    CoverPageHeader,
    Footer,
    Header,
    PageReferencesProvider,
    TableOfContents,
} from "./shared";
import RenderSections, {
    buildExportSectionMeta,
    buildExportTocEntries,
} from "./sections";
import styles from "./styles";

registerExportFonts();

const SupplierDocument = (props) => {
    const {
        supplierData,
        user,
        downloadDate,
        overviewHeader,
        statistics,
        lastActivities,
        riskMatrixData,
    } = props;

    const { primaryColor, stopColor, logo } = useBranding();
    const isOwnCompany = !!supplierData.supplierDetail?.isOwnCompany;

    const dateUtils = new DateUtils();
    const reportDownloadDate = downloadDate
        || dateUtils.convertDateToDate(now(), INTERNATIONAL_DATE_TIME_FORMAT);

    const exportContext = {
        kybReports: supplierData?.kybReports,
    };
    const sectionMeta = buildExportSectionMeta(exportContext);
    const tocEntries = buildExportTocEntries(exportContext);

    const sectionProps = {
        sectionMeta,
        supplierData,
        user,
        downloadDate: reportDownloadDate,
        overviewHeader,
        statistics,
        lastActivities,
        riskMatrixData,
        isOwnCompany,
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <CoverPage
                    supplierDetail={{
                        ...(supplierData.supplierDetail || {}),
                        connectedLegalEntities: supplierData.connectedLegalEntities || [],
                    }}
                    user={user}
                    downloadDate={reportDownloadDate}
                    logo={logo}
                    primaryColor={primaryColor}
                    startColor={primaryColor}
                    stopColor={stopColor}
                    isOwnCompany={isOwnCompany}
                />
                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <CoverPageHeader
                    backgroundColor={primaryColor}
                    logo={logo}
                />
                <RenderSections placement="management-summary" {...sectionProps} />
                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <Header
                    startColor={primaryColor}
                    stopColor={stopColor}
                    backgroundColor={primaryColor}
                    logo={logo}
                />
                <TableOfContents
                    entries={tocEntries}
                    subtitle={[
                        supplierData?.supplierDetail?.name,
                        supplierData?.supplierDetail?.id
                            ? `Report ID ${supplierData.supplierDetail.id}`
                            : null,
                    ].filter(Boolean).join(" | ") || undefined}
                />
                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <Header
                    startColor={primaryColor}
                    stopColor={stopColor}
                    backgroundColor={primaryColor}
                    logo={logo}
                />
                <View>
                    <RenderSections placement="overview" {...sectionProps} />
                </View>
                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <Header
                    startColor={primaryColor}
                    stopColor={stopColor}
                    backgroundColor={primaryColor}
                    logo={logo}
                />
                <View>
                    <RenderSections placement="body" {...sectionProps} />
                </View>
                <Footer backgroundColor={primaryColor} />
            </Page>
        </Document>
    );
};

const Supplier = (props) => {
    const { organization, supplierData } = props;
    const pageReferencesRef = useRef(new Map());

    if (!supplierData) {
        return (
            <Document>
                <Page size="A4" style={styles.page1} />
            </Document>
        );
    }

    return (
        <BrandingProvider organization={organization}>
            <PageReferencesProvider pageReferences={pageReferencesRef}>
                <SupplierDocument {...props} />
            </PageReferencesProvider>
        </BrandingProvider>
    );
};

export default withNamespaces()(Supplier);
